import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const UnitOfMeasure = () => {
  const { canWrite } = useAuth();
  const [units, setUnits] = useState([]);
  const [viewItem, setViewItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/units-of-measure`);
      setUnits(response.data || []);
    } catch (error) {
      console.error('Error loading units of measure:', error);
      toast.error('Failed to load units of measure');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (unit = null) => {
    setEditingUnit(unit);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      unit_code: (formData.get('unit_code') || '').toUpperCase(),
      commercial: (formData.get('commercial') || '').toUpperCase(),
      technical: (formData.get('technical') || '').toUpperCase(),
      description: formData.get('description')
    };

    try {
      if (editingUnit) {
        await axios.put(`${BACKEND_URL}/api/units-of-measure/${editingUnit.unit_code}`, {
          commercial: data.commercial,
          technical: data.technical,
          description: data.description
        });
        toast.success('Unit of Measure updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/units-of-measure`, data);
        toast.success('Unit of Measure created successfully');
      }
      setIsDialogOpen(false);
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save unit of measure');
    }
  };

  const handleDelete = async (unitCode) => {
    if (!window.confirm(`Are you sure you want to delete Unit '${unitCode}'?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/units-of-measure/${unitCode}`);
      toast.success('Unit of Measure deleted successfully');
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete unit of measure');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Base Unit of Measure</h1>
            <p className="text-sm text-gray-500 mt-1">Manage units of measurement for materials</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Units of Measure</h2>
            {canWrite('unit_of_measure') && (
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Unit
            </Button>
            )}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Unit Code</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Commercial</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Technical</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {units.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-500">
                        No units found. Click &quot;Add Unit&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    units.map((unit) => (
                      <tr key={unit.unit_code} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{unit.unit_code}</td>
                        <td className="py-3 px-4 text-sm font-mono">{unit.commercial}</td>
                        <td className="py-3 px-4 text-sm font-mono">{unit.technical}</td>
                        <td className="py-3 px-4 text-sm">{unit.description}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-2 justify-end">
                            <RecordActions
                              doctype="unit_of_measure"
                              idForTestId={unit.unit_code}
                              onView={() => setViewItem(unit)}
                              onEdit={() => handleOpenDialog(unit)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Unit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingUnit ? 'Edit Unit of Measure' : 'Add Unit of Measure'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="unit_code">Unit Code * (3 characters)</Label>
                  <Input
                    id="unit_code"
                    name="unit_code"
                    defaultValue={editingUnit?.unit_code || ''}
                    placeholder="e.g., PC, KG, EA"
                    maxLength={3}
                    minLength={1}
                    pattern="[A-Za-z0-9]{1,3}"
                    title="Must be 1-3 alphanumeric characters"
                    disabled={!!editingUnit}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 3 alphanumeric characters</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingUnit?.description || ''}
                    placeholder="e.g., Piece, Kilogram"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Commercial Unit *</Label>
                  <Input
                    name="commercial"
                    defaultValue={editingUnit?.commercial || ''}
                    placeholder="e.g., PC"
                    maxLength={10}
                    className="uppercase"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Unit code for commercial use</p>
                </div>
                <div>
                  <Label>Technical Unit *</Label>
                  <Input
                    name="technical"
                    defaultValue={editingUnit?.technical || ''}
                    placeholder="e.g., PC"
                    maxLength={10}
                    className="uppercase"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Unit code for technical use</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingUnit ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={`Unit of Measure ${viewItem?.unit_code || ''}`}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default UnitOfMeasure;
